<template>
  <v-container fluid
               class="pb-0 pr-0"
               style="height: calc(100vh - 64px);">
    <PageTitle icon="fab fa-fw fa-slideshare">
      Presentation Builder
      <template v-slot:buttons>
        <template v-if="hasSlides">
          <v-dialog v-model="saveDialog"
                    max-width="800"
                    persistent>
            <template v-slot:activator="{on, attrs}">
              <v-btn color="primary"
                     class="mt-1"
                     v-bind="attrs"
                     v-on="on">
                Save
              </v-btn>
            </template>
            <v-form @submit.prevent="saveSlideDeck"
                    :disabled="form.busy"
                    autocomplete="off">
              <v-card flat>
                <v-card-title class="text-h5 primary white--text">
                  Save Custom Presentation
                </v-card-title>
                <div class="pa-4">
                  <v-text-field v-model="form.name"
                                label="Name (Must be unique)"
                                placeholder="Must be unique"
                                autocomplete="off"
                                outlined
                                dense
                                required
                                :error-messages="form.errors.get('name')">
                  </v-text-field>

                  <div class="d-flex">
                    <v-switch v-model="form.is_favorite"
                              label="Add to favorites"
                              class="mt-0">
                    </v-switch>
                  </div>

                  <div>
                    Your custom presentation is private unless an administrator shares it with your colleagues.
                  </div>
                </div>
                <v-card-actions class="px-4">
                  <v-btn type="submit"
                         color="primary"
                         :disabled="form.busy || !form.name">
                    Save
                  </v-btn>
                  <v-btn type="button"
                         @click.prevent="closeSaveDialog"
                         :disabled="form.busy">
                    Cancel
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-form>
          </v-dialog>
        </template>
      </template>
    </PageTitle>

    <div class="PresentationBuilder__Container">
      <div class="PresentationBuilder__ScrollingColumn PresentationBuilder__PresentationColumn">
        <v-card v-for="presentation in sourceDecks"
                :key="presentation.id"
                class="mb-4"
                :elevation="presentationID === presentation.id ? 8 : 0"
                @click.prevent="selectPresentation(presentation.id)">

          <div v-if="presentation.thumbnail"
               class="pa-1 grey d-flex justify-center align-center"
               style="position: relative;">
            <v-img :src="presentation.thumbnail.url"
                   :alt="presentation.name"
                   contain
                   max-width="130"
                   class="PresentationBuilder__Thumbnail">
            </v-img>

            <v-tooltip v-if="presentation.owner_id === user.id"
                       top>
              <template v-slot:activator="{on, attrs}">
                <v-icon color="black"
                        class="white pa-1"
                        style="position: absolute; right: 4px; top: 4px;"
                        v-bind="attrs"
                        v-on="on">
                  fal fa-user-lock
                </v-icon>
              </template>
              <div>
                This is private to you.
              </div>
            </v-tooltip>
          </div>
          <div class="text-caption pa-1">
            {{ presentation.name }}
          </div>
        </v-card>
      </div>

      <v-divider class="mr-2" vertical></v-divider>

      <template v-if="presentationLoading || presentation">
        <div class="PresentationBuilder__ScrollingColumn PresentationBuilder__SlideColumn">
          <div v-if="presentationLoading"
               class="d-flex justify-center flex-1">
            <v-progress-circular indeterminate
                                 size="80">
            </v-progress-circular>
          </div>
          <template v-else>
            <div class="d-flex flex-column">
              <v-btn @click.prevent="addAll"
                     class="mb-2"
                     color="primary"
                     outlined
                     block>
                Add all
              </v-btn>
              <v-btn @click.prevent="removeAll"
                     class="mb-2"
                     color="primary"
                     outlined
                     block>
                Remove all
              </v-btn>
            </div>
            <div style="overflow-x: hidden;">
              <v-card v-for="slide in activeSlides"
                      :key="slide.id"
                      class="mb-4">
                <div class="pa-1 grey d-flex justify-center align-center position-relative">
                  <v-img :src="slide.image_url"
                         :alt="slide.name"
                         contain
                         max-width="130"
                         class="PresentationBuilder__Thumbnail"
                         @click.prevent="viewSlide(slide.id)">
                  </v-img>
                  <v-icon v-if="slide.instructions"
                          title="This slide has instructions."
                          color="warning"
                          class="pa-1"
                          style="background: #FFFFFF; position: absolute; top: 0; left: 0;">
                    fas fa-fw fa-exclamation-triangle
                  </v-icon>
                  <v-icon v-if="hasNoteFor(slide.id)"
                          title="You have a note for this slide."
                          color="#FFD600"
                          class="pa-1"
                          style="background: #FFFFFF; position: absolute; top: 0; right: 0;">
                    fas fa-fw fa-sticky-note
                  </v-icon>
                </div>
                <v-card-text class="py-0 px-2 text-center">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                      <div v-on="on">
                        {{ truncateText(slide.name, 28) }}
                      </div>
                    </template>
                    <div>
                      {{ slide.name }}
                    </div>
                  </v-tooltip>
                </v-card-text>
                <v-card-text v-if="slide.is_required"
                             class="py-0 px-2 text-center">
                  <div class="font-weight-bold red--text text-uppercase">
                    Required slide
                  </div>
                </v-card-text>
                <div class="pa-2 d-flex justify-space-between align-center">
                  <v-btn v-if="slideIncluded(slide)"
                         color="primary"
                         class="flex-grow-1"
                         outlined
                         :disabled="slide.is_required"
                         @click.prevent="removeSlide(slide)">
                    Remove
                  </v-btn>
                  <v-btn v-else
                         class="flex-grow-1"
                         outlined
                         @click.prevent="addSlide(slide, true)">
                    Add
                  </v-btn>
                  <v-btn v-if="1===2"
                         class="ms-1"
                         icon
                         @click.prevent="viewSlide(slide.id)">
                    <v-icon small>
                      fas fa-fw fa-eye
                    </v-icon>
                  </v-btn>
                </div>
              </v-card>
            </div>
          </template>
        </div>
        <v-divider class="mr-2" vertical></v-divider>
        <div ref="thirdColumn"
             class="PresentationBuilder__ThirdColumn">
          <v-card v-if="presentation && presentation.markdown_builder_instructions"
                  class="mb-4">
            <v-card-title>
              {{ presentation.name}}
            </v-card-title>
            <v-card-subtitle>
              Instructions
            </v-card-subtitle>
            <v-card-text>
              <div v-html="presentation.markdown_builder_instructions">
              </div>
            </v-card-text>
          </v-card>
          <template v-if="hasSlides">
            <draggable v-model="form.slides"
                       class="PresentationBuilder__SlideArea"
                       handle=".handle">
              <v-card v-for="(slide, index) in form.slides"
                      :key="slide.id"
                      class="PresentationBuilder__Slide PresentationBuilder__Draggable">
                <div class="pa-1 grey fill-height d-flex align-center">
                  <div class="d-flex flex-column fill-height flex-grow-1 justify-center align-center">
                    <div class="d-flex flex-column justify-center flex-grow-1 fill-height position-relative">
                      <v-img :src="slide.image_url"
                             :alt="slide.name"
                             contain
                             :width="slideWidth"
                             class="handle">
                      </v-img>

                      <v-icon v-if="slide.instructions"
                              @click.prevent="viewSlide(slide.id)"
                              title="This slide has instructions."
                              color="warning"
                              class="pa-1"
                              style="background: #FFFFFF; position: absolute; top: 0; left: 0;">
                        fas fa-fw fa-exclamation-triangle
                      </v-icon>
                      <v-icon v-if="hasNoteFor(slide.id)"
                              @click.prevent="viewSlide(slide.id)"
                              title="You have a note for this slide."
                              color="#FFD600"
                              class="pa-1"
                              style="background: #FFFFFF; position: absolute; top: 0; right: 0;">
                        fas fa-fw fa-sticky-note
                      </v-icon>
                    </div>
                    <div class="d-flex justify-space-between align-center pt-2"
                         style="width: 100%;">
                      <div>
                        <v-icon class="handle">
                          fal fa-fw fa-grip-vertical
                        </v-icon>
                        <v-icon>
                          fal fa-fw
                        </v-icon>
                      </div>
                      <div class="d-flex justify-center align-center pt-2 white--text font-weight-bold">
                        #{{ (index + 1) }}
                      </div>
                      <div>
                        <v-btn @click.prevent="viewSlide(slide.id)"
                               icon>
                          <v-icon>
                            fal fa-fw fa-info-circle
                          </v-icon>
                        </v-btn>
                        <v-btn v-if="slide.is_required === false"
                               icon
                               @click.prevent="removeSlide(slide)">
                          <v-icon>
                            fas fa-fw fa-trash
                          </v-icon>
                        </v-btn>
                        <v-btn v-else
                               icon
                               @click.prevent=""
                               color="red">
                          <v-icon color="red"
                                  title="This is a required slide.">
                            fal fa-fw fa-asterisk
                          </v-icon>
                        </v-btn>
                      </div>
                    </div>
                    <div class="text-center white--text font-weight-bold"
                         style="flex-grow: 1; flex-shrink: 0;">
                      {{ truncateText(slide.name, 38) }}
                    </div>
                  </div>
                </div>
              </v-card>
            </draggable>
          </template>
        </div>
      </template>


    </div>

    <ShowSlideDialog v-if="showSlideDialog && showSlide"
                     :dialog="showSlideDialog"
                     :slide="showSlide"
                     @closed="closeSlideDialog">
    </ShowSlideDialog>
  </v-container>
</template>
<script>
import PageTitle from '../../components/PageTitle.vue';
import {Form} from 'vform';
import {find, isInteger, orderBy, truncate} from 'lodash';
import draggable from 'vuedraggable';
import presentationService from '../../../../ml/services/presentationService';
import {mapGetters} from 'vuex';
import slideDeckService from '../../services/slideDeckService';
import PresentationFormLabels from '../presentations/components/PresentationFormLabels.vue';
import ShowSlideDialog from './components/ShowSlideDialog.vue';

export default {
  components: {ShowSlideDialog, draggable, PresentationFormLabels, PageTitle},
  data() {
    return {
      addAllSlides: false,
      showSlideDialog: false,
      saveDialog: false,
      form: new Form({
        name: null,
        slides: [],
        presentation_type: 'PDF',
        is_slide_deck: true,
        is_custom: true, // MLD-3281
        is_favorite: true,
        settings: {
          can_be_presented_by_user: true,
          can_be_shared: true,
          can_be_shared_link: true,

          // User permissions
          can_be_exported: true, // MLD-3281
          can_be_printed_by_user: true, // MLD-3281
          can_be_used_in_builder: true, // MLD-3874

          // Recipient permissions
          can_be_printed: true, // MLD-3281
          can_be_downloaded: true, // MLD-3281
          can_be_forwarded: false, // MLD-3281
        }
      }),
      notes: [],
      presentationLoading: null,
      presentationID: '',
      presentation: null,
      slideContainerHeight: '',
      slides: [],
      showSlide: null,
      columnWidth: 0,
    }
  },
  computed: {
    ...mapGetters([
      'user',
    ]),
    ...mapGetters('content', [
      'bookmarksFolder',
      'folders',
      'presentations',
      'SORT_OPTION_NAME',
      'SORT_OPTION_DATE',
    ]),
    hasSlides() {
      return this.form.slides.length > 0
    },
    sourceDecks() {
      const decks = this.presentations.filter(p => {
        return p.settings?.can_be_used_in_builder && (p.type_pdf || p.converts_to_pdf);
      });
      return orderBy(decks, ['slug', 'name'], ['asc', 'asc']); // MLD-7300
    },
    sourceDeckID() {
      if (this.$route.query.source_deck_id) {
        try {
          return parseInt(this.$route.query.source_deck_id);
        } catch (e) {
        }
      }
      return null;
    },
    activeSlides() {
      return this.slides.filter(s => s.is_active);
    },
    requiredSlides() {
      return this.slides.filter(s => s.is_required);
    },
    hasRequiredSlides() {
      return this.requiredSlides.length > 0;
    },
    selectedSlideIDs() {
      return this.form.slides.map(s => s.id);
    },
    thirdColumnWidth() {
      return this.columnWidth;
    },
    slideWidth() {
      if (this.thirdColumnWidth > 0) {
        return Math.max(Math.floor(this.thirdColumnWidth / 3) - 30, 260);
      }
      return 400;
    },
  },
  created() {
    this.fetchData();
    if (this.sourceDeckID) {
      this.selectPresentation(this.sourceDeckID)
    }
  },
  mounted() {
    this.updateColumnWidth();
    window.addEventListener('resize', this.updateColumnWidth);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateColumnWidth);
  },
  methods: {
    getNoteFor(slide) {
      const slideNote = find(this.notes, n => n.slide_id === slide.id);
    },
    updateColumnWidth() {
      if (this.$refs.thirdColumn) {
        this.columnWidth = this.$refs.thirdColumn.offsetWidth;
        //console.log('Updated column width', this.columnWidth);
      }
    },
    fetchData() {
      slideDeckService.getNotesForCurrentUser()
        .then(data => {
          this.notes = orderBy(data, ['slide_id'], ['asc']);
        })
    },
    hasNoteFor(slideID) {
      const note = find(this.notes, n => n.slide_id === slideID);
      return !!note;
    },
    async viewSlide(slideID) {
      try {
        this.showSlide = await slideDeckService.getSlide(slideID);
        this.showSlideDialog = true;
      } catch (e) {
        this.showSlide = null;
        this.showSlideDialog = false;
      }
    },
    closeSlideDialog() {
      this.showSlide = null;
      this.showSlideDialog = false;
      this.fetchData();
    },
    addAll() {
      if (!this.presentation) {
        return;
      }
      this.slides.forEach(slide => {
        this.addSlide(slide, false);
      });
    },
    removeAll() {
      if (!this.presentation) {
        return;
      }
      this.form.slides.forEach(this.removeSlide);
    },
    toggleSlide(slide) {
      if (this.slideIncluded(slide)) {
        if (slide.is_required === false) {
          this.removeSlide(slide);
        }
      } else {
        this.addSlide(slide, true);
      }
    },
    slideIncluded(slide) {
      return this.selectedSlideIDs.includes(slide.id);
    },
    addSlide(slide, includeRequired) {
      this.closeSlideDialog();
      if (slide.is_active === false) {
        return;
      }
      if (this.slideIncluded(slide)) {
        return;
      }
      /*if (this.hasSlides === false) {
        const now = moment().format('YYYY-MM-DD hh:mm a');
        this.form.name = this.presentation ? `${this.presentation.name} - ${now}` : '';
      }*/
      this.form.slides.push(slide);
      if (includeRequired && this.hasRequiredSlides) {
        this.requiredSlides.forEach(s => {
          if (s.id !== slide.id) {
            this.addSlide(s, false);
          }
        });
      }
      this.updateColumnWidth()
    },
    removeSlide(slide) {
      if (!this.slideIncluded(slide)) {
        // Slide is not already included.
        return;
      }
      this.form.slides = this.form.slides.filter(s => s.id !== slide.id);
      this.updateColumnWidth()
    },
    async selectPresentation(presentationID) {
      if (isInteger(presentationID) === false) {
        return;
      }
      this.addAllSlides = false;
      this.presentationID = presentationID;
      this.presentationLoading = presentationID;
      this.presentation = await presentationService.getPresentation(this.presentationID);

      this.slides = await slideDeckService.getSlidesForPresentation(this.presentation.id);
      //this.slides = orderBy(slides, ['position', 'id'], ['asc', 'asc']);
      this.presentationLoading = null;
    },
    saveSlideDeck() {
      this.form.startProcessing();
      //console.log(this.form.data());
      const form = this.form.data();
      form.slides = form.slides.map(s => s.id);

      this.form.post(`/api/slide-decks`, {data: form})
        .then(response => {
          this.$store.dispatch('content/setActiveFolderID', this.bookmarksFolder.id);
          this.$router.push({name: `${this.$root.routePrefix}.home`})
        })
        .catch(err => {

        })
    },
    truncateText(text, length = 30) {
      const options = {
        length,
        omission: '...',
      }
      return truncate(text, options);
    },
    showSaveDialog() {
      this.saveDialog = true;
    },
    closeSaveDialog() {
      this.saveDialog = false;
    }
  }
}
</script>
